import {
  Paper,
  Box,
  Typography,
  Divider,
  CircularProgress,
  useTheme,
  Chip,
} from "@mui/material"
import { ChatViewer } from "../interfaces"
import { Message } from "./Message"
import { UserGroupsAccordion } from "./UserGroupsAccordion"
import { PurchaseInfoAccordion } from "./PurchaseInfoAccordion"
import { useRef, useEffect } from "react"

interface MessageViewerProps {
  chat: ChatViewer.Chat | null
  loading?: boolean
}

export const MessageViewer: React.FC<MessageViewerProps> = ({
  chat,
  loading = false,
}) => {
  const theme = useTheme()
  const messagesBoxRef = useRef<HTMLDivElement>(null)
  const paperRef = useRef<HTMLDivElement>(null)

  // При смене чата: прокручиваем страницу к просмотрщику, затем список сообщений — вниз
  useEffect(() => {
    if (!chat) return

    // 1. Прокрутить страницу к верхней границе просмотрщика
    if (paperRef.current) {
      const top =
        paperRef.current.getBoundingClientRect().top + window.scrollY - 128
      window.scrollTo({ top, behavior: "smooth" })
    }

    // 2. Прокрутить контейнер сообщений к последнему сообщению
    if (messagesBoxRef.current) {
      messagesBoxRef.current.scrollTop = messagesBoxRef.current.scrollHeight
    }
  }, [chat?.chat_id])

  if (loading) {
    return (
      <Paper
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Paper>
    )
  }

  if (!chat) {
    return (
      <Paper
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Box textAlign="center">
          <Typography variant="body1" color="text.secondary">
            Выберите чат для просмотра сообщений
          </Typography>
        </Box>
      </Paper>
    )
  }

  const bgColor =
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.grey[50]

  return (
    <Paper
      ref={paperRef}
      sx={{ height: "70vh", display: "flex", flexDirection: "column" }}
    >
      {/* Header */}
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <Typography variant="h6" gutterBottom>
          Chat ID: {chat.chat_id}
        </Typography>
        {!!chat.user_info && (
          <>
            <Typography variant="caption" color="text.secondary">
              Telegram ID: {chat.user_info.telegram_id}
            </Typography>
            <Chip
              label={chat.user_info.message_type}
              size="small"
              sx={{ ml: 1 }}
              color="primary"
              variant="outlined"
            />
          </>
        )}

        {/* Группы пользователя */}
        <UserGroupsAccordion groups={chat.user_groups} />

        {/* Информация о покупках */}
        {!!chat.purchase_info && (
          <PurchaseInfoAccordion purchaseInfo={chat.purchase_info} />
        )}
      </Box>

      <Divider />

      {/* Messages */}
      <Box
        ref={messagesBoxRef}
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          display: "flex",
          flexDirection: "column",
          backgroundColor: bgColor,
        }}
      >
        {chat.messages.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Нет сообщений в этом чате
            </Typography>
          </Box>
        ) : (
          chat.messages.map((message, index) => (
            <Message key={index} message={message} />
          ))
        )}
      </Box>
    </Paper>
  )
}
